import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PartnerLinkError, type PartnerValidationIssue } from '../api/partnerLinks'
import { partnerLinkKeys } from './usePartnerLink'

/** What a card needs to act for the link: the token to renew the session, and how to reload. */
export interface PartnerLinkSession {
  /** Link token held in memory; empty after a reload. */
  token: string
  slug: string
  /** The property ID in the URL path, which keys the page's query. */
  pathPropertyId: string
  /** Shows the page's "out of date" notice. */
  onStale: () => void
}

/**
 * How the page answers a failed mutation:
 * - `stale`: the server refused because the page is out of date (400, 403, 409,
 *   410). The view is reloaded and the page's notice shown.
 * - `session`: the session is gone and could not be renewed (a final 401). The
 *   view is reloaded, which shows the page-level card.
 * - `invalid`: the server rejected the fields (422). Shown on the form.
 * - `temporary`: the network or the service failed. Shown on the card, form kept.
 */
export type PartnerMutationFailure = 'stale' | 'session' | 'invalid' | 'temporary'

const STALE_STATUSES: ReadonlySet<number> = new Set([400, 403, 409, 410])

export function mutationFailureOf(error: unknown): PartnerMutationFailure {
  if (!(error instanceof PartnerLinkError) || error.status === undefined) return 'temporary'
  if (STALE_STATUSES.has(error.status)) return 'stale'
  if (error.status === 401) return 'session'
  if (error.status === 422) return 'invalid'
  return 'temporary'
}

/** The 422 field list of an error, or an empty list. */
export function validationIssuesOf(error: unknown): PartnerValidationIssue[] {
  return error instanceof PartnerLinkError && Array.isArray(error.detail) ? error.detail : []
}

/** FastAPI prefixes validator messages with "Value error, "; the reader does not need it. */
export function readableIssue(issue: PartnerValidationIssue): string {
  return issue.msg.replace(/^Value error, /, '')
}

/**
 * A mutation on the partner page.
 *
 * Success reloads the page's one query, and the mutation stays pending until
 * that reload finishes, so a submit button stays disabled until the new state
 * is on screen. A stale refusal reloads it too and raises the page's notice; a
 * lost session reloads it so the page shows the card a fresh open would.
 */
export function usePartnerMutation<TVariables, TData>(
  session: PartnerLinkSession,
  mutationFn: (variables: TVariables) => Promise<TData>
) {
  const queryClient = useQueryClient()
  const queryKey = partnerLinkKeys.view(session.slug, session.pathPropertyId)

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      const failure = mutationFailureOf(error)
      if (failure === 'stale') {
        session.onStale()
      }
      if (failure === 'stale' || failure === 'session') {
        return queryClient.invalidateQueries({ queryKey })
      }
      return undefined
    },
  })
}
